import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { ArrowOutward } from '@mui/icons-material';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { Box, RefBox } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { safeWindowOpen } from '@utils/urlSanitization';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type IUrlOption = {
  link: string;
  label: TranslatedString;
};

type IQuickLinksOptions = {
  url1: IUrlOption;
  url2: IUrlOption;
  url3: IUrlOption;
  url4: IUrlOption;
  url5: IUrlOption;
  url6: IUrlOption;
};

const Link = ({ link, label }: IUrlOption) => {
  const { sys } = useThemeTokens();
  const { showToast } = useGlobalToast();
  return (
    <Card
      sx={{
        flex: 1,
        maxHeight: 72,
        minWidth: 165,
        boxShadow: sys.elevation.low,
      }}
      hover
      onClick={() =>
        safeWindowOpen(link, {
          target: '_blank',
          onError: (errorMsg) => {
            showToast({ severity: 'error', message: errorMsg });
          },
        })
      }
    >
      <CardContent sx={{ padding: 3 }}>
        <Box
          display='flex'
          flexDirection='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography
            variant='bodyLarge'
            weight='bold'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {translateBackend(label)}
          </Typography>
          <ArrowOutward sx={{ fontSize: 20 }} />
        </Box>
      </CardContent>
    </Card>
  );
};

const defaultLabel = getLanguageDefaults();

export const QuickLinks = ({
  options = {
    url1: {
      link: '',
      label: defaultLabel,
    },
    url2: {
      link: '',
      label: defaultLabel,
    },
    url3: {
      link: '',
      label: defaultLabel,
    },
    url4: {
      link: '',
      label: defaultLabel,
    },
    url5: {
      link: '',
      label: defaultLabel,
    },
    url6: {
      link: '',
      label: defaultLabel,
    },
  },
}: {
  options: IQuickLinksOptions;
}) => {
  const { url1, url2, url3, url4, url5, url6 } = options;
  const { sys } = useThemeTokens();
  const ref = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const updateHasOverflow = () => {
      if (ref.current)
        setHasOverflow(
          (ref.current.scrollWidth ?? 0) > (ref.current.clientWidth ?? 0),
        );
    };
    updateHasOverflow();
    window.addEventListener('resize', updateHasOverflow);
    return () => window.removeEventListener('resize', updateHasOverflow);
  }, [ref]);

  return (
    <RefBox
      ref={ref}
      display='flex'
      flexDirection='row'
      justifyContent='space-between'
      alignItems='center'
      gap={2}
      sx={{
        overflowX: 'auto',
        paddingBottom: 0.5,
        '&:hover': {
          paddingBottom: hasOverflow ? 0 : undefined,
        },
        '&::-webkit-scrollbar': {
          height: '4px',
          display: 'none',
        },
        '&:hover::-webkit-scrollbar': {
          display: 'block',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: `${sys.color.onSurfaceVariant}33`,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: `${sys.color.onSurfaceVariant}4D`,
          borderRadius: '4px',
        },
      }}
    >
      {url1.link && <Link link={url1.link} label={url1.label} />}
      {url2.link && <Link link={url2.link} label={url2.label} />}
      {url3.link && <Link link={url3.link} label={url3.label} />}
      {url4.link && <Link link={url4.link} label={url4.label} />}
      {url5.link && <Link link={url5.link} label={url5.label} />}
      {url6.link && <Link link={url6.link} label={url6.label} />}
    </RefBox>
  );
};
